// src/Component/LanguageDropdown.jsx
import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage, SUPPORTED_LANGUAGES } from '../Component/context/Languagecontext';
import { useParams } from 'react-router-dom';

// ─────────────────────────────────────────────────────────────────
// Single source of truth — edit colors here only
// bg   → button trigger background (gradient or solid)
// text → lang code text color on trigger button
// grad → [startColor, endColor] for selected item gradient effects
// ─────────────────────────────────────────────────────────────────
const CATEGORY_THEME = {
  "RnJpZW5k_love": { bg: "#FF617A", text: "#FF617A", grad: ["#FF617A", "#FF617A"] },
  "RnJpZW5k_friend": { bg: "#B3ADFD", text: "#B3ADFD", grad: ["#B3ADFD", "#B3ADFD"] },
  "V2hvVGFsa2lu": { bg: "linear-gradient(to right, #684BB9, #684BB9)", text: "#684BB9", grad: ["#684BB9", "#684BB9"] },
  "UGljIFJvYXN0": { bg: "linear-gradient(to right, #2F80ED, #2F80ED)", text: "#2F80ED", grad: ["#2F80ED", "#2F80ED"] },
  "QW5ub3kgZnVuIENhcmQ=": { bg: "linear-gradient(to right, #FDA881, #FF207B)", text: "#FF207B", grad: ["#FDA881", "#FF207B"] },
  "RW1vdGlvbg==": { bg: "linear-gradient(to right, #FFA400, #FFA400)", text: "#FFA400", grad: ["#FFA400", "#FFA400"] },
  "Q29uZmVzc2lvbg==": { bg: "#8001CE", text: "#8001CE", grad: ["#8001CE", "#8001CE"] },
  "SG90bmVzcw==": { bg: "linear-gradient(to right, #FB2A1B, #FC8B5C)", text: "#FB2A1B", grad: ["#FB2A1B", "#FC8B5C"] },
  "Um9hc3Q=": { bg: "#F98423", text: "#F98423", grad: ["#F98423", "#F98423"] },
  "UnVtb3Vy": { bg: "linear-gradient(to left, #03C2FE 0%, #DE67FD 100%)", text: "#DE67FD", grad: ["#DE67FD", "#03C2FE"] },
  "Q2hhbGxlbmdl": { bg: "#7D5EEE", text: "#7D5EEE", grad: ["#7D5EEE", "#7D5EEE"] },
  "SGVhdmVuSGVsbA==": { bg: "#00C9EB", text: "#00C9EB", grad: ["#00C9EB", "#00C9EB"] },
  "UmVwdXRhdGlvbg==": { bg: "linear-gradient(to right, #703AFC, #EB7C69)", text: "#EB7C69", grad: ["#703AFC", "#EB7C69"] },
  "SW1wcmVzc2lvbg==": { bg: "linear-gradient(to right, #FFB244, #FF408B)", text: "#FF408B", grad: ["#FFB244", "#FF408B"] },
};

const DEFAULT_THEME = {
  bg: "linear-gradient(to right, #684BB9, #684BB9)",
  text: "#684BB9",
  grad: ["#684BB9", "#684BB9"],
};

// resolves the theme object from sessionStorage
const resolveTheme = (category, subCat) => {
  if (category === "RnJpZW5k") {
    return subCat !== "RnJpZW5k"
      ? CATEGORY_THEME["RnJpZW5k_love"]
      : CATEGORY_THEME["RnJpZW5k_friend"];
  }

  return CATEGORY_THEME[category] || DEFAULT_THEME;
};

// extract a solid color from bg (fallback for single-color themes)
const extractSolid = (bg) => {
  const match = bg.match(/#[0-9a-fA-F]{6}/g);
  return match ? match[match.length - 1] : bg;
};

// build a CSS linear-gradient string from grad array
// direction: "to right" | "to bottom" | "135deg" etc.
const makeGradient = (grad, direction = "135deg") => {
  if (!grad || grad.length < 2 || grad[0] === grad[1]) {
    return grad ? grad[0] : "#684BB9";
  }
  return `linear-gradient(${direction}, ${grad[0]}, ${grad[1]})`;
};

// Unique SVG gradient id per theme (avoids conflicts between renders)
let _gradientCounter = 0;

// ── icons ─────────────────────────────────────────────────────────
const TranslateIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
    xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
    <path d="M3 5H13M8 3V5M10.5 5C10.5 9 7.5 13 3 14.5"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M6 10C6.5 11.5 8 14 11 15.5M12 21L17 9L22 21M13.5 17.5H20.5"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronIcon = ({ open }) => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      transition: 'transform 0.25s ease',
      transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
      flexShrink: 0
    }}>
    <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2.5"
      strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Radio-button style: gradient ring border + small gradient filled circle inside
const RadioIcon = ({ grad, gradientId, isSelected }) => {
  const isSingle = !grad || grad.length < 2 || grad[0] === grad[1];
  const gradColor = isSingle ? grad[0] : `url(#${gradientId})`;
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      {isSelected && !isSingle && (
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={grad[0]} />
            <stop offset="100%" stopColor={grad[1]} />
          </linearGradient>
        </defs>
      )}
      {/* Outer ring — gradient when selected, dark border when not */}
      <circle
        cx="10" cy="10" r="8.5"
        stroke={isSelected ? gradColor : "rgba(0,0,0,1)"}
        strokeWidth="1.8"
        fill="none"
      />
      {/* Inner dot — only shown when selected */}
      {isSelected && (
        <circle cx="10" cy="10" r="4.5" fill={gradColor} />
      )}
    </svg>
  );
};

// ── component ─────────────────────────────────────────────────────
const LanguageDropdown = ({ subCat }) => {
  const { lanText, setUserLanguage } = useLanguage();
  const [isDisabled, setIsDisabled] = useState(false);
  const { category } = useParams();
  const [open, setOpen] = useState(false);
  const [theme, setTheme] = useState(resolveTheme(category, subCat));
  // Stable gradient id for SVG defs (won't change between renders for same instance)
  const gradientId = useRef(`lang-grad-${++_gradientCounter}`).current;
  const ref = useRef(null);

  const current = SUPPORTED_LANGUAGES.find(l => l.code === lanText) || SUPPORTED_LANGUAGES[0];

  // listen for category change event
  useEffect(() => {
    const update = () => setTheme(resolveTheme(category, subCat));
    update();
    window.addEventListener("dropdownCategoryChanged", update);
    return () => window.removeEventListener("dropdownCategoryChanged", update);
  }, [category, subCat]);

  // close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      setIsDisabled(e.detail.disabled);
      if (e.detail.disabled) setOpen(false); // force close if open
    };
    window.addEventListener('dropdownDisabledState', handler);
    return () => window.removeEventListener('dropdownDisabledState', handler);
  }, []);

  const handleSelect = (code) => {
    setUserLanguage(code);
    setOpen(false);
  };

  // Gradient CSS for selected text (webkit gradient clip trick)
  const gradientTextStyle = (grad) => {
    const g = makeGradient(grad, "135deg");
    const isSingle = !grad || grad.length < 2 || grad[0] === grad[1];
    if (isSingle) {
      return { color: grad ? grad[0] : extractSolid(theme.bg) };
    }
    return {
      background: g,
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      backgroundClip: "text",
      color: "transparent",
    };
  };

  // Selected row background: soft gradient tint (10% opacity overlay)
  const selectedRowBg = (grad) => {
    const isSingle = !grad || grad.length < 2 || grad[0] === grad[1];
    const solid = grad ? grad[grad.length - 1] : extractSolid(theme.bg);
    if (isSingle) return `${solid}18`;
    // Use a very light tinted gradient for the row bg
    return `linear-gradient(135deg, ${grad[0]}18, ${grad[1]}18)`;
  };

  return (
    <div className='GTRegular' ref={ref} style={{
      position: 'fixed', top: '8px', right: '10px',
      zIndex: 10000, userSelect: 'none',
    }}>

      {/* ── Trigger button ── */}
      <button
        onClick={() => !isDisabled && setOpen(prev => !prev)}
        style={{
          display: 'flex', alignItems: 'center', gap: '3px',
          padding: '2px 5px 2px 2px',
          background: 'white',
          backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
          border: '1.5px solid rgba(0,0,0,0.08)', borderRadius: '999px',
          boxShadow: '0 2px 1px rgba(0,0,0,0.05)',
          cursor: 'pointer', color: '#1a1a1a',
          fontSize: '13px', letterSpacing: '0.3px',
          outline: 'none', transition: 'box-shadow 0.2s ease',
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          pointerEvents: isDisabled ? 'none' : 'auto',
        }}
      >
        <div style={{
          background: '#DFDFDF',
          borderRadius: '50%',
          padding: '6px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <TranslateIcon />
        </div>
        <span className='pt-1'>{current.display}</span>
        <ChevronIcon open={open} />
      </button>

      {/* ── Dropdown panel ── */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.95 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            style={{
              position: 'absolute', top: 'calc(100% + 8px)', right: 0,
              minWidth: '175px',
              background: 'rgba(255,255,255,0.97)',
              backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
              borderRadius: '18px', boxShadow: '0 2px 12px rgba(0,0,0,0.16)',
              border: '1.5px solid rgba(0,0,0,0.07)',
              overflow: 'hidden', padding: '6px',
            }}
          >
            {SUPPORTED_LANGUAGES.map((lang, idx) => {
              const isSelected = lang.code === lanText;
              const grad = theme.grad;
              // Unique gradient id per row to avoid SVG defs collision
              const rowGradId = `${gradientId}-row-${idx}`;

              return (
                <div
                  key={lang.code}
                  onClick={() => handleSelect(lang.code)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '12px',
                    padding: '10px 12px', borderRadius: '12px', cursor: 'pointer',
                    // ✅ Gradient tinted background for selected row
                    background: isSelected ? selectedRowBg(grad) : 'transparent',
                    transition: 'background 0.15s ease', marginBottom: '2px',
                  }}
                >
                  {/* Badge — gradient bg when selected */}
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: '34px', height: '34px', borderRadius: '10px',
                    // ✅ Full gradient on selected badge (uses theme.bg which already has gradient)
                    background: isSelected ? theme.bg : '#F0F0F0',
                    color: isSelected ? '#fff' : '#555',
                    fontSize: '14px', letterSpacing: '0.4px',
                    paddingTop: "2px",
                    flexShrink: 0, transition: 'all 0.2s ease',
                  }}>
                    {lang.display}
                  </span>

                  {/* Label — gradient text when selected */}
                  <span style={{
                    flex: 1, fontSize: '16px',
                    transition: 'color 0.15s ease',
                    // ✅ Gradient text for selected label
                    ...(isSelected ? gradientTextStyle(grad) : { color: '#1a1a1a' }),
                  }}>
                    {lang.label}
                  </span>

                  <RadioIcon grad={grad} gradientId={rowGradId} isSelected={isSelected} />

                </div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LanguageDropdown;